import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatInr, paise } from '@bits/domain';
import type { LeadChannelSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const crmChannelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/channels',
  component: CrmChannelsPage,
});

const MONTHS = 6;

function lastNMonths(n: number): string[] {
  const now = new Date();
  const istMs = now.getTime() + (5 * 60 + 30) * 60 * 1000;
  const cur = new Date(istMs);
  const y = cur.getUTCFullYear();
  const m = cur.getUTCMonth();
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - i, 1));
    const yy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    out.push(`${yy}-${mm}`);
  }
  return out;
}

function CrmChannelsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<LeadChannelSummary | null>(null);
  const [editError, setEditError] = React.useState<string | null>(null);

  const channelsQuery = useQuery({ queryKey: ['channels'], queryFn: () => api.listLeadChannels() });
  const spendQuery = useQuery({
    queryKey: ['spend', MONTHS],
    queryFn: () => api.getChannelSpend(MONTHS),
  });

  const months = lastNMonths(MONTHS);
  const channels = channelsQuery.data ?? [];
  const spend = spendQuery.data ?? [];
  const spendMap = new Map<string, number>();
  for (const s of spend) {
    spendMap.set(`${s.channelId}::${s.month}`, s.amountPaise);
  }

  const addMutation = useMutation({
    mutationFn: () => api.upsertLeadChannel({ name: newName, active: true }),
    onSuccess: async () => {
      setAddOpen(false);
      setNewName('');
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const editMutation = useMutation({
    mutationFn: (v: { id: string; name: string; active: boolean }) =>
      api.updateLeadChannel(v.id, { name: v.name, active: v.active }),
    onSuccess: async () => {
      setEditing(null);
      setEditError(null);
      await queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (err) => {
      setEditError(toMessage(err));
    },
  });

  const updateSpendMutation = useMutation({
    mutationFn: (v: { channelId: string; month: string; amountPaise: number }) =>
      api.upsertChannelSpend(v.channelId, { month: v.month, amountPaise: v.amountPaise }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['spend', MONTHS] });
    },
  });

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Channels</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Lead sources plus monthly ad-spend. Feeds CAC in Analytics.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button>Add channel</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New channel</DialogTitle>
              <DialogDescription>Names are unique; adding an existing name reactivates it.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Name</Label>
                <Input value={newName} onChange={(e) => setNewName(e.currentTarget.value)} />
              </div>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button disabled={addMutation.isPending || !newName} onClick={() => addMutation.mutate()}>
                  Save channel
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Spend grid</CardTitle>
          <CardDescription>Amounts in paise. Editing writes on blur.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {channels.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No channels yet.</p>
          ) : (
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[hsl(var(--border))] px-3 py-2 text-left font-medium">
                    Channel
                  </th>
                  {months.map((m) => (
                    <th
                      key={m}
                      className="border-b border-[hsl(var(--border))] px-3 py-2 text-right font-medium"
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channels.map((c) => (
                  <tr key={c.id} className="border-b border-[hsl(var(--border))]">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="rounded px-1 text-left hover:bg-[hsl(var(--accent))]"
                        onClick={() => {
                          setEditing(c);
                          setEditError(null);
                        }}
                      >
                        <span className={c.active ? undefined : 'text-[hsl(var(--muted-foreground))]'}>
                          {c.name}
                        </span>
                        {c.active ? null : (
                          <span className="ml-2 rounded-full border border-[hsl(var(--border))] px-2 py-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                            Inactive
                          </span>
                        )}
                      </button>
                    </td>
                    {months.map((m) => {
                      const key = `${c.id}::${m}`;
                      const amount = spendMap.get(key) ?? 0;
                      const display = formatInr(paise(BigInt(amount)));
                      return (
                        <td key={m} className="px-3 py-2 text-right">
                          <SpendCell
                            display={display}
                            initial={amount}
                            onCommit={(next) => {
                              if (next === amount) return;
                              updateSpendMutation.mutate({
                                channelId: c.id,
                                month: m,
                                amountPaise: next,
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing ? (
        <EditChannelDialog
          key={editing.id}
          channel={editing}
          pending={editMutation.isPending}
          errorMessage={editError}
          onCancel={() => {
            setEditing(null);
            setEditError(null);
          }}
          onSave={(name, active) => editMutation.mutate({ id: editing.id, name, active })}
        />
      ) : null}
    </AppShell>
  );
}

function EditChannelDialog({
  channel,
  pending,
  errorMessage,
  onCancel,
  onSave,
}: {
  channel: LeadChannelSummary;
  pending: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onSave: (name: string, active: boolean) => void;
}): JSX.Element {
  const [name, setName] = React.useState(channel.name);
  const [active, setActive] = React.useState(channel.active);
  const unchanged = name === channel.name && active === channel.active;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit channel</DialogTitle>
          <DialogDescription>
            Renaming keeps all history and spend. Inactive channels stay on past reports.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
            />
            Active
          </label>
          {errorMessage ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button disabled={pending || !name || unchanged} onClick={() => onSave(name, active)}>
              Save changes
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SpendCell({
  display,
  initial,
  onCommit,
}: {
  display: string;
  initial: number;
  onCommit: (nextPaise: number) => void;
}): JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(String(initial));
  React.useEffect(() => {
    setValue(String(initial));
  }, [initial]);
  if (!editing) {
    return (
      <button
        type="button"
        className="w-full text-right hover:bg-[hsl(var(--accent))] rounded px-1"
        onClick={() => setEditing(true)}
      >
        {display}
      </button>
    );
  }
  return (
    <input
      autoFocus
      type="number"
      className="w-32 rounded border border-[hsl(var(--input))] px-2 py-1 text-right text-sm"
      value={value}
      onChange={(e) => setValue(e.currentTarget.value)}
      onBlur={() => {
        setEditing(false);
        const n = Number(value);
        if (Number.isFinite(n) && Number.isInteger(n) && n >= 0) onCommit(n);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValue(String(initial));
          setEditing(false);
        }
      }}
    />
  );
}
